// Вкладка «Пользователи»: список, создание (временный пароль - показать один раз), изменение квоты и имени,
// роль (нельзя снять права с себя и последнего администратора - сервер ответит 409), блокировка, сброс пароля,
// завершение сеансов, удаление (восстановимо) и окончательное удаление - с подтверждением вводом логина.
import { useMemo, useState } from 'react';
import type { DialogProps, MenuItemSpec } from '../../core/registry';
import { ctx } from '../../core/ctx';
import { askConfirm } from '../../ui/prompts';
import { Btn, Field, MenuList, Modal, Popover, Segmented } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { Avatar } from '../auth/account';
import { ROLE_NAMES, useAuth } from '../auth/session';
import { LOGIN_HINT, LOGIN_RE } from '../auth/fields';
import { adminApi, type AUser } from './adminApi';
import { act, askTyped, fmtAgo, fmtDate, LoadError, Pill, RowMenuButton, showSecret, SortTh, useLoad, UsageBar, type Sort } from './common';

type Filter = 'all' | 'admin' | 'blocked' | 'deleted' | 'nosub';
type Key = 'name' | 'role' | 'projects' | 'used' | 'last';

export function UsersTab() {
  const me = useAuth((s) => s.me);
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort<Key>>({ key: 'name', dir: 1 });
  const [menu, setMenu] = useState<{ el: HTMLElement; u: AUser } | null>(null);
  const { data, error, loading, reload } = useLoad(() => adminApi.users(true), []);
  const owner = data?.owner ?? null;
  const all = data?.users || [];
  const counts = {
    all: all.filter((u) => !u.deleted).length,
    admin: all.filter((u) => !u.deleted && u.role === 'admin').length,
    blocked: all.filter((u) => !u.deleted && u.blocked).length,
    deleted: all.filter((u) => u.deleted).length,
    nosub: all.filter((u) => !u.deleted && !u.subscription.active).length,
  };
  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let arr = all.filter((u) => (filter === 'deleted' ? !!u.deleted : !u.deleted)
      && (filter !== 'admin' || u.role === 'admin') && (filter !== 'blocked' || u.blocked) && (filter !== 'nosub' || !u.subscription.active));
    if (ql) arr = arr.filter((u) => `${u.login} ${u.name}`.toLowerCase().includes(ql));
    const by: Record<Key, (a: AUser, b: AUser) => number> = {
      name: (a, b) => (a.name || a.login).localeCompare(b.name || b.login, 'ru'),
      role: (a, b) => a.role.localeCompare(b.role) || a.login.localeCompare(b.login),
      projects: (a, b) => a.projects - b.projects,
      used: (a, b) => a.usedBytes - b.usedBytes,
      last: (a, b) => (a.lastLogin || 0) - (b.lastLogin || 0),
    };
    return [...arr].sort((a, b) => by[sort.key](a, b) * sort.dir);
  }, [all, filter, q, sort]);

  const create = async () => {
    const r = await ctx.openDialog<{ user: AUser; tempPassword?: string }>('admin.userCreate');
    if (!r) return;
    await reload();
    if (r.tempPassword) {
      void showSecret({ title: 'Пользователь создан', secret: r.tempPassword, testid: 'temp-password-dialog',
        text: <>Передайте пользователю <b>{r.user.login}</b> логин и временный пароль. При первом входе он придумает свой.</>,
        note: 'Пароль показывается один раз. Потеряли - «Сбросить пароль» выдаст новый.' });
    } else ctx.toast(`Пользователь ${r.user.login} создан`, 'ok');
  };

  const items = (u: AUser): MenuItemSpec[] => {
    const self = u.id === me?.user.id;
    if (u.deleted) {
      return [
        { id: 'user-restore', label: 'Восстановить с отчётами', icon: 'restore', onClick: () => void act(() => adminApi.restoreUser(u.id), `${u.login} восстановлен`, reload) },
        { separator: true },
        { id: 'user-purge', label: 'Удалить окончательно…', icon: 'trash', onClick: async () => {
          const ok = await askTyped({ title: 'Удалить окончательно?', word: u.login, ok: 'Удалить навсегда', testid: 'purge-user-confirm',
            text: <>Учётная запись <b>{u.login}</b> и все её отчёты будут стёрты с диска <b>без возможности восстановления</b>.</> });
          if (ok) void act(() => adminApi.purgeUser(u.id, u.login), 'Удалено окончательно', reload);
        } },
      ];
    }
    return [
      { id: 'user-edit', label: 'Изменить имя и квоты…', icon: 'user-cog', onClick: async () => { if (await ctx.openDialog('admin.userEdit', { user: u })) void reload(); } },
      { id: 'user-subscription', label: 'Подписка…', icon: 'star', onClick: async () => { if (await ctx.openDialog('admin.userSubscription', { user: u })) void reload(); } },
      { id: 'user-referrer', label: u.referrer ? `Кто пригласил: ${u.referrer}…` : 'Кто пригласил…', icon: 'users', onClick: async () => { if (await ctx.openDialog('admin.userReferrer', { user: u })) void reload(); } },
      { id: 'user-projects', label: `Отчёты пользователя (${u.projects})`, icon: 'file', onClick: () => { location.hash = '#/admin/projects?owner=' + u.id; } },
      { separator: true },
      u.role === 'admin'
        ? { id: 'user-demote', label: 'Снять права администратора', icon: 'user', disabled: self, hint: self ? 'Нельзя снять права с самого себя' : undefined,
          onClick: async () => {
            if (await askConfirm({ title: 'Снять права администратора?', text: `${u.login} станет обычным пользователем и увидит только свои отчёты.`, ok: 'Снять права' })) {
              void act(() => adminApi.updateUser(u.id, { role: 'user' }), 'Права администратора сняты', reload);
            }
          } }
        : { id: 'user-promote', label: 'Сделать администратором', icon: 'shield-user', onClick: async () => {
          if (await askConfirm({ title: 'Сделать администратором?', text: `${u.login} получит доступ ко всем отчётам и к управлению пользователями.`, ok: 'Сделать администратором', danger: true })) {
            void act(() => adminApi.updateUser(u.id, { role: 'admin' }), `${u.login} - администратор`, reload);
          }
        } },
      u.blocked
        ? { id: 'user-unblock', label: 'Разблокировать', icon: 'unlock', onClick: () => void act(() => adminApi.updateUser(u.id, { blocked: false }), `${u.login} разблокирован`, reload) }
        : { id: 'user-block', label: 'Заблокировать', icon: 'ban', disabled: self, hint: self ? 'Нельзя заблокировать самого себя' : undefined, onClick: async () => {
          if (await askConfirm({ title: `Заблокировать ${u.login}?`, text: 'Пользователь сразу выйдет на всех устройствах и не сможет войти, пока вы его не разблокируете. Отчёты сохранятся.', ok: 'Заблокировать', danger: true, testid: 'block-confirm' })) {
            void act(() => adminApi.updateUser(u.id, { blocked: true }), `${u.login} заблокирован`, reload);
          }
        } },
      { id: 'user-reset', label: 'Сбросить пароль…', icon: 'key', onClick: async () => {
        if (!await askConfirm({ title: `Сбросить пароль ${u.login}?`, text: 'Будет выдан временный пароль (его нужно сменить при входе); текущие сеансы пользователя завершатся.', ok: 'Сбросить пароль', testid: 'reset-confirm' })) return;
        try {
          const r = await adminApi.resetPassword(u.id);
          await reload();
          void showSecret({ title: 'Новый временный пароль', secret: r.tempPassword, testid: 'temp-password-dialog',
            text: <>Передайте пользователю <b>{u.login}</b> временный пароль. При входе он придумает свой.</>,
            note: 'Пароль показывается один раз.' });
        } catch (e: any) { ctx.toast(e.message, 'error'); }
      } },
      { id: 'user-sessions', label: `Завершить сеансы (${u.sessions})`, icon: 'logout', disabled: !u.sessions,
        onClick: () => void act(() => adminApi.revokeSessions(u.id), 'Сеансы завершены', reload) },
      { separator: true },
      { id: 'user-delete', label: 'Удалить…', icon: 'user-x', disabled: self || u.id === owner, hint: self ? 'Нельзя удалить самого себя' : undefined, onClick: async () => {
        const ok = await askTyped({ title: `Удалить пользователя ${u.login}?`, word: u.login, ok: 'Удалить', testid: 'delete-user-confirm',
          text: <>Учётная запись и <b>все отчёты</b> ({u.projects}) будут удалены. В течение срока хранения удалённых (настройка) их можно
            восстановить во вкладке «Пользователи → Удалённые».</> });
        if (ok) void act(() => adminApi.deleteUser(u.id, u.login), `${u.login} удалён`, reload);
      } },
    ];
  };

  return (
    <section className="adm-tab" data-testid="admin-users">
      <div className="adm-toolbar">
        <div className="search-box"><Icon name="search" size={14} />
          <input className="search" placeholder="Поиск по логину или имени" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" aria-label="Поиск пользователя" />
        </div>
        <Segmented value={filter} onChange={setFilter} testid="users-filter" options={[
          { id: 'all', label: `Все · ${counts.all}` }, { id: 'admin', label: `Администраторы · ${counts.admin}` },
          { id: 'blocked', label: `Заблокированные · ${counts.blocked}` }, { id: 'nosub', label: `Без подписки · ${counts.nosub}` }, { id: 'deleted', label: `Удалённые · ${counts.deleted}` },
        ]} />
        <span className="topbar-spacer" />
        <Btn icon="refresh" title="Обновить" onClick={() => void reload()} className={loading ? 'is-loading' : ''} />
        <Btn icon="user-plus" label="Создать пользователя" primary onClick={create} testid="user-create" />
      </div>
      <LoadError error={error} reload={reload} />
      <div className="adm-table-wrap">
        <table className="adm-table" data-testid="users-table">
          <thead><tr>
            <SortTh k="name" sort={sort} setSort={setSort}>Пользователь</SortTh>
            <SortTh k="role" sort={sort} setSort={setSort}>Роль</SortTh>
            <th>Состояние</th>
            <th>Подписка</th>
            <SortTh k="projects" sort={sort} setSort={setSort} className="num">Отчёты</SortTh>
            <SortTh k="used" sort={sort} setSort={setSort}>Место</SortTh>
            <SortTh k="last" sort={sort} setSort={setSort} className="adm-col-opt">Последний вход</SortTh>
            <th className="num adm-col-opt">Сеансы</th>
            <th className="adm-col-act" aria-label="Действия" />
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-testid="user-row" data-login={u.login} className={(u.blocked ? 'is-blocked ' : '') + (u.deleted ? 'is-deleted' : '')}>
                <td>
                  <div className="adm-user">
                    <Avatar user={u} size={30} />
                    <div className="adm-user-text">
                      <div className="adm-user-name">{u.name || u.login}
                        {u.id === me?.user.id && <span className="adm-tag">это вы</span>}
                        {u.id === owner && <span className="adm-tag" title="Создан при первом запуске: ему - данные writer/defaults.yaml">владелец сервера</span>}
                      </div>
                      <div className="adm-user-login">{u.login} · с {fmtDate(u.created)}</div>
                    </div>
                  </div>
                </td>
                <td>{u.role === 'admin' ? <Pill tone="info"><Icon name="shield-user" size={12} /> {ROLE_NAMES.admin}</Pill> : <span className="adm-dim">{ROLE_NAMES.user}</span>}</td>
                <td>
                  {u.deleted ? <Pill tone="err">Удалён · стирается {fmtDate(u.purgeAt)}</Pill>
                    : u.blocked ? <Pill tone="err" testid="user-blocked"><Icon name="ban" size={12} /> Заблокирован</Pill>
                      : u.mustChangePassword ? <Pill tone="warn"><Icon name="key" size={12} /> Временный пароль</Pill>
                        : <Pill tone="ok">Активен</Pill>}
                </td>
                <td data-testid="user-sub"><SubCell u={u} /></td>
                <td className="num">{u.projects}{u.maxProjects ? <span className="adm-dim"> / {u.maxProjects}</span> : ''}</td>
                <td><UsageBar used={u.usedBytes} total={u.quotaBytes} />{u.quotaCustom && <span className="adm-tag" title="Своя квота">своя</span>}</td>
                <td className="adm-col-opt" title={u.lastLogin ? fmtDate(u.lastLogin, true) : ''}>{fmtAgo(u.lastLogin)}</td>
                <td className="num adm-col-opt">{u.sessions || <span className="adm-dim">0</span>}</td>
                <td className="adm-col-act"><RowMenuButton testid={'user-menu-' + u.login} onOpen={(el) => setMenu({ el, u })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !rows.length && (
          <div className="adm-empty"><Icon name={filter === 'deleted' ? 'trash' : 'users'} size={26} />
            <div>{q ? `Никого не найдено по запросу «${q}»` : filter === 'deleted' ? 'Удалённых пользователей нет' : filter === 'blocked' ? 'Заблокированных нет' : 'Пока никого нет'}</div>
            {filter === 'all' && !q && <Btn icon="user-plus" label="Создать пользователя" onClick={create} />}
          </div>
        )}
      </div>
      {menu && (
        <Popover anchor={menu.el} onClose={() => setMenu(null)} align="right">
          <MenuList items={items(menu.u)} onDone={() => setMenu(null)} autoFocus />
        </Popover>
      )}
    </section>
  );
}

function SubCell({ u }: { u: AUser }) {
  const s = u.subscription;
  if (s.exempt) return <span className="adm-dim">не нужна</span>;
  if (s.lifetime) return <Pill tone="ok">Пожизненная</Pill>;
  if (s.active && s.until) return <Pill tone={s.until - Date.now() / 1000 < 7 * 86400 ? 'warn' : 'ok'}>до {new Date(s.until * 1000).toLocaleDateString('ru-RU')}</Pill>;
  if (s.until) return <Pill tone="err">истекла {new Date(s.until * 1000).toLocaleDateString('ru-RU')}</Pill>;
  return <Pill tone="dim">Нет</Pill>;
}

// ------------------------------------------------------------------------------------------------ окна
/** Подписка пользователя: продлить на дни или месяцы, задать дату окончания, пожизненная, отменить. */
export function UserReferrerDialog({ props, close }: DialogProps<boolean>) {
  const u = props.user as AUser;
  const [login, setLogin] = useState(u.referrer || '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setErr('');
    setBusy(true);
    try {
      await adminApi.setReferrer(u.id, login.trim());
      ctx.toast('Сохранено', 'ok');
      close(true);
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };
  return (
    <Modal title={`Кто пригласил: ${u.login}`} onClose={() => close()} width={460} testid="user-referrer-dialog" footer={<>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Сохранить" primary onClick={() => void save()} disabled={busy} testid="user-referrer-save" />
    </>}>
      <Field label="Ник пригласившего" hint="пусто - никто; прошлые начисления не меняются">
        <input value={login} onChange={(e) => setLogin(e.target.value)} autoFocus data-testid="user-referrer-login" />
      </Field>
      {err && <div className="adm-error" role="alert" data-testid="user-referrer-error"><Icon name="error" size={16} /><span>{err}</span></div>}
    </Modal>
  );
}

export function UserSubscriptionDialog({ props, close }: DialogProps<boolean>) {
  const u = props.user as AUser;
  const s = u.subscription;
  const [mode, setMode] = useState<'extend' | 'until' | 'lifetime' | 'cancel'>('extend');
  const [amount, setAmount] = useState('30');
  const [unit, setUnit] = useState<'days' | 'months'>('days');
  const [paid, setPaid] = useState('');
  const [date, setDate] = useState(() => {
    const d = new Date(Math.max(Date.now(), (s.until || 0) * 1000) + 30 * 86400000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setErr('');
    setBusy(true);
    try {
      const paidRub = mode === 'cancel' ? 0 : Number(paid) || 0;
      await adminApi.subscription(u.id, mode === 'extend' ? { action: 'extend', [unit]: Number(amount), paidRub } : mode === 'until' ? { action: 'until', date, paidRub } : { action: mode, paidRub });
      ctx.toast('Подписка изменена', 'ok');
      close(true);
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };
  const now = s.exempt ? 'администратору подписка не нужна' : s.lifetime ? 'пожизненная' : s.active && s.until ? `до ${new Date(s.until * 1000).toLocaleDateString('ru-RU')}`
    : s.until ? `истекла ${new Date(s.until * 1000).toLocaleDateString('ru-RU')}` : 'нет';
  return (
    <Modal title={`Подписка: ${u.login}`} onClose={() => close()} width={560} testid="user-sub-dialog" footer={<>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label={mode === 'cancel' ? 'Отменить подписку' : 'Применить'} primary={mode !== 'cancel'} danger={mode === 'cancel'} onClick={save} disabled={busy} testid="user-sub-save" />
    </>}>
      <p>Сейчас: <b data-testid="user-sub-now">{now}</b></p>
      <div className="form-grid">
        <Field label="Действие" wide>
          <Segmented value={mode} onChange={setMode} testid="user-sub-mode" options={[
            { id: 'extend', label: 'Продлить' }, { id: 'until', label: 'До даты' }, { id: 'lifetime', label: 'Пожизненная' }, { id: 'cancel', label: 'Отменить' }]} />
        </Field>
        {mode === 'extend' && <>
          <Field label="На сколько" hint="от конца текущего срока, если он ещё не прошёл"><input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="user-sub-amount" /></Field>
          <Field label="Единица">
            <select value={unit} onChange={(e) => setUnit(e.target.value as 'days' | 'months')} data-testid="user-sub-unit">
              <option value="days">дней</option>
              <option value="months">месяцев</option>
            </select>
          </Field>
        </>}
        {mode !== 'cancel' && <Field label="Оплачено, ₽" hint="сколько заплатил пользователь; пусто - подарок: пригласившему ничего не начисляется">
          <input type="number" min={0} value={paid} onChange={(e) => setPaid(e.target.value)} data-testid="user-sub-paid" />
        </Field>}
        {mode === 'until' && <Field label="Дата окончания" hint="последний день подписки"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="user-sub-date" /></Field>}
      </div>
      {mode === 'cancel' && <p className="adm-note"><Icon name="info" size={14} /> Пользователь сможет только смотреть свои отчёты.</p>}
      {err && <div className="adm-error" role="alert" data-testid="user-sub-error"><Icon name="error" size={16} /><span>{err}</span></div>}
    </Modal>
  );
}

export function CreateUserDialog({ close }: DialogProps<{ user: AUser; tempPassword?: string }>) {
  const [login, setLogin] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [mode, setMode] = useState<'temp' | 'set'>('temp');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setErr('');
    if (!LOGIN_RE.test(login.trim())) { setErr('Логин: ' + LOGIN_HINT.toLowerCase()); return; }
    if (mode === 'set' && pw.length < 8) { setErr('Пароль - не короче 8 символов'); return; }
    setBusy(true);
    try {
      const r = await adminApi.createUser({ login: login.trim(), name: name.trim(), role, password: mode === 'set' ? pw : undefined, mustChange: true });
      close(r);
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };
  return (
    <Modal title="Новый пользователь" onClose={() => close()} width={520} testid="user-create-dialog" footer={<>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Создать" primary onClick={submit} disabled={busy} testid="user-create-submit" />
    </>}>
      <div className="form-grid" onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); void submit(); } }}>
        <Field label="Логин" hint={LOGIN_HINT}><input value={login} onChange={(e) => setLogin(e.target.value)} data-testid="new-user-login" autoComplete="off" spellCheck={false} /></Field>
        <Field label="Имя и фамилия"><input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name" autoComplete="off" /></Field>
        <Field label="Роль" wide>
          <Segmented value={role} onChange={setRole} testid="new-user-role" options={[
            { id: 'user', label: 'Пользователь - только свои отчёты', icon: 'user' }, { id: 'admin', label: 'Администратор', icon: 'shield-user' }]} />
        </Field>
        <Field label="Пароль" wide>
          <Segmented value={mode} onChange={setMode} testid="new-user-pwmode" options={[
            { id: 'temp', label: 'Выдать временный', icon: 'key' }, { id: 'set', label: 'Задать самому', icon: 'rename' }]} />
        </Field>
        {mode === 'set' && (
          <Field label="Начальный пароль" wide hint="Пользователь сменит его при первом входе">
            <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-user-password" autoComplete="off" spellCheck={false} />
          </Field>
        )}
      </div>
      {mode === 'temp' && <p className="adm-note"><Icon name="info" size={14} /> Временный пароль покажем один раз после создания; при первом входе пользователь придумает свой.</p>}
      {err && <div className="adm-error" role="alert" data-testid="user-create-error"><Icon name="error" size={16} /><span>{err}</span></div>}
    </Modal>
  );
}

export function EditUserDialog({ props, close }: DialogProps<boolean>) {
  const u = props.user as AUser;
  const [name, setName] = useState(u.name || '');
  const [ownQuota, setOwnQuota] = useState(u.quotaCustom);
  const [quota, setQuota] = useState(String(u.quotaBytes ? Math.round(u.quotaBytes / 1048576) : 1024));
  const [ownMax, setOwnMax] = useState(u.maxProjects !== null && u.role !== 'admin');
  const [maxP, setMaxP] = useState(String(u.maxProjects ?? 100));
  const [err, setErr] = useState('');
  const save = async () => {
    try {
      await adminApi.updateUser(u.id, { name: name.trim(), quota_mb: ownQuota ? Number(quota) : null, max_projects: ownMax ? Number(maxP) : null });
      ctx.toast('Сохранено', 'ok');
      close(true);
    } catch (e: any) { setErr(e.message); }
  };
  return (
    <Modal title={`Пользователь ${u.login}`} onClose={() => close()} width={520} testid="user-edit-dialog" footer={<>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Сохранить" primary onClick={save} testid="user-edit-save" />
    </>}>
      <div className="form-grid">
        <Field label="Имя и фамилия" wide><input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" /></Field>
        <label className="check field-wide"><input type="checkbox" checked={ownQuota} onChange={(e) => setOwnQuota(e.target.checked)} data-testid="edit-own-quota" /> Своя квота места (иначе - общая из настроек)</label>
        {ownQuota && <Field label="Место, МБ"><input type="number" min={1} value={quota} onChange={(e) => setQuota(e.target.value)} data-testid="edit-quota" /></Field>}
        <label className="check field-wide"><input type="checkbox" checked={ownMax} onChange={(e) => setOwnMax(e.target.checked)} /> Свой предел числа отчётов</label>
        {ownMax && <Field label="Отчётов, не больше"><input type="number" min={1} value={maxP} onChange={(e) => setMaxP(e.target.value)} /></Field>}
      </div>
      {u.role === 'admin' && <p className="adm-note"><Icon name="info" size={14} /> У администратора общих пределов нет; своя квота действует, если задана.</p>}
      {err && <div className="adm-error" role="alert"><Icon name="error" size={16} /><span>{err}</span></div>}
    </Modal>
  );
}
